import { Link } from "react-router-dom";
import { SEO } from "@/components/seo";
import { CTASection } from "@/components/common/CTASection";
import {
  FiArrowRight,
  FiCheck,
  FiAlertCircle,
  FiTrendingDown,
  FiDollarSign,
  FiHome,
  FiPercent,
  FiRefreshCw,
} from "@/lib/icons";

const reasonsToRefinance = [
  {
    title: "Lower your interest rate",
    description:
      "Even a small rate reduction can save thousands over the life of your loan. If rates have dropped or you've been on the same rate for years, you could be paying more than you need to.",
    icon: <FiTrendingDown className="size-6" />,
    example: "0.5% lower on a $500K loan = ~$1,500/year saved",
  },
  {
    title: "Access your equity",
    description:
      "If your property value has increased, you may be able to access the equity for renovations, investments, or other purposes.",
    icon: <FiHome className="size-6" />,
    example: "Property up $100K = potential $80K accessible equity",
  },
  {
    title: "Consolidate debts",
    description:
      "Roll high-interest debts (credit cards, personal loans) into your home loan at a much lower rate. Just be mindful of extending the term.",
    icon: <FiDollarSign className="size-6" />,
    example: "18% credit card → 6% home loan rate",
  },
  {
    title: "Change loan features",
    description:
      "Switch to a loan with better features like an offset account, redraw facility, or more flexible repayment options.",
    icon: <FiPercent className="size-6" />,
    example: "Offset account can save thousands in interest",
  },
  {
    title: "Fixed rate ending",
    description:
      "When your fixed term expires, your loan typically reverts to a higher variable rate. This is the perfect time to shop around.",
    icon: <FiRefreshCw className="size-6" />,
    example: "Don't accept the revert rate – compare first",
  },
  {
    title: "Change your loan term",
    description:
      "Extend to lower repayments, or shorten to pay off faster and save on interest. Your circumstances may have changed since you first borrowed.",
    icon: <FiArrowRight className="size-6" />,
    example: "Shorten by 5 years = major interest savings",
  },
];

const refinancingProcess = [
  {
    step: 1,
    title: "Review your current loan",
    description:
      "Check your current interest rate, loan balance, and any features you're using. Look for any exit fees or break costs (especially for fixed loans).",
    duration: "Day 1",
  },
  {
    step: 2,
    title: "Get a comparison",
    description:
      "We'll compare your current loan against options from 70+ lenders to see what savings are possible. This is free and no-obligation.",
    duration: "Day 1-2",
  },
  {
    step: 3,
    title: "Calculate the costs vs savings",
    description:
      "Factor in any discharge fees, new loan setup costs, and potential LMI. We'll show you the break-even point and total savings.",
    duration: "Day 2-3",
  },
  {
    step: 4,
    title: "Submit your application",
    description:
      "If refinancing makes sense, we'll prepare and submit your application. You'll need income documents, ID, and details of your current loan.",
    duration: "Day 3-7",
  },
  {
    step: 5,
    title: "Valuation and assessment",
    description:
      "The new lender will value your property and assess your application. We'll track progress and handle any questions.",
    duration: "Week 1-2",
  },
  {
    step: 6,
    title: "Approval and settlement",
    description:
      "Once approved, you'll sign new loan documents. The new lender pays out your old loan, and the switch is complete.",
    duration: "Week 2-4",
  },
];

const costs = [
  {
    name: "Discharge fee",
    typical: "$150 - $400",
    description: "Fee from your current lender to release your mortgage",
  },
  {
    name: "Application/setup fee",
    typical: "$0 - $750",
    description: "Many lenders waive this for refinancers",
  },
  {
    name: "Valuation fee",
    typical: "$0 - $600",
    description: "Often waived or covered by the new lender",
  },
  {
    name: "Settlement fee",
    typical: "$200 - $500",
    description: "Legal/conveyancing costs for the transfer",
  },
  {
    name: "Break costs (fixed loans)",
    typical: "Varies widely",
    description: "Can be significant – always check before refinancing",
  },
  {
    name: "Lenders Mortgage Insurance",
    typical: "If LVR > 80%",
    description: "May apply if you have less than 20% equity",
  },
];

const warningSignsToRefinance = [
  "Your rate is more than 0.5% above current market rates",
  "You've been with the same lender for 3+ years without a rate review",
  "Your fixed rate period is ending soon",
  "Your property has increased significantly in value",
  "Your income has improved and you could get better terms",
  "You're paying high-interest debts alongside your mortgage",
  "Your current loan lacks features you need (offset, redraw)",
  "You've received cashback or rate offers from other lenders",
];

const whenNotToRefinance = [
  {
    scenario: "High break costs on fixed loans",
    why: "Break costs can exceed any savings. Always calculate the total cost first.",
  },
  {
    scenario: "Less than 2 years into your loan",
    why: "You may not have built enough equity, and setup costs may outweigh short-term savings.",
  },
  {
    scenario: "Planning to sell soon",
    why: "If you're selling within 1-2 years, refinancing costs may not be recovered.",
  },
  {
    scenario: "Credit issues since your original loan",
    why: "New lenders will assess your current situation. If your credit has worsened, you may not get a better deal.",
  },
  {
    scenario: "Minimal savings",
    why: "If the rate difference is small and fees are high, it may not be worth the hassle.",
  },
];

export function RefinancingGuidePage() {
  return (
    <>
      <SEO
        title="Refinancing Guide - When and How to Refinance Your Home Loan"
        description="Complete guide to refinancing your home loan in Australia. Learn when to refinance, how the process works, costs involved, and how much you could save."
        url="/guides/refinancing-guide"
      />

      {/* Hero */}
      <section className="bg-cream py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Link
            to="/guides"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-6 transition-colors"
          >
            <FiArrowRight className="size-4 mr-2 rotate-180" />
            Back to Guides
          </Link>
          <p className="section-label mb-4">Guide</p>
          <h1 className="font-serif text-4xl md:text-5xl mb-6">
            Refinancing Guide
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl">
            Refinancing could save you thousands – or it could cost you. This
            guide helps you understand when refinancing makes sense, how the
            process works, and what to watch out for.
          </p>
          <div className="flex items-center gap-4 mt-6 text-sm text-muted-foreground">
            <span>6 min read</span>
            <span>•</span>
            <span>Updated January 2026</span>
          </div>
        </div>
      </section>

      {/* Key stat */}
      <section className="py-12 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <p className="text-primary-foreground/90 mb-2">Average potential savings</p>
          <p className="font-serif text-4xl md:text-5xl mb-2">$3,000+ per year</p>
          <p className="text-primary-foreground/90 text-sm">
            Based on 0.5% rate reduction on $600,000 loan
          </p>
        </div>
      </section>

      {/* Reasons to refinance */}
      <section className="py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="mb-12">
            <p className="section-label mb-4">Why Refinance</p>
            <h2 className="font-serif text-3xl md:text-4xl">
              Reasons to consider refinancing
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {reasonsToRefinance.map((reason) => (
              <div
                key={reason.title}
                className="bg-white rounded-2xl p-6 border border-border/50"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-accent/20 rounded-xl flex items-center justify-center text-primary">
                    {reason.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">{reason.title}</h3>
                    <p className="text-muted-foreground text-sm mb-3">
                      {reason.description}
                    </p>
                    <p className="text-xs bg-muted px-3 py-1.5 rounded-lg inline-block">
                      {reason.example}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Warning signs checklist */}
      <section className="py-16 md:py-20 bg-cream">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="mb-8">
            <p className="section-label mb-4">Self-Assessment</p>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">
              Signs it's time to refinance
            </h2>
            <p className="text-muted-foreground">
              If any of these apply to you, it's worth getting a comparison.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-6 md:p-8 border border-border/50">
            <div className="grid md:grid-cols-2 gap-4">
              {warningSignsToRefinance.map((sign, index) => (
                <label
                  key={sign}
                  className="flex items-start gap-3 cursor-pointer group"
                >
                  <input
                    type="checkbox"
                    id={`sign-${index}`}
                    className="flex-shrink-0 w-5 h-5 rounded border-border text-primary focus:ring-primary focus:ring-offset-0 mt-0.5 cursor-pointer"
                  />
                  <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                    {sign}
                  </span>
                </label>
              ))}
            </div>
            <div className="mt-6 pt-6 border-t border-border">
              <p className="text-sm text-muted-foreground">
                Ticked more than 2? You could be leaving money on the table.{" "}
                <Link
                  to="/contact"
                  className="text-primary font-medium hover:underline"
                >
                  Get a free comparison
                </Link>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="mb-12">
            <p className="section-label mb-4">The Process</p>
            <h2 className="font-serif text-3xl md:text-4xl">
              How refinancing works
            </h2>
          </div>

          <div className="space-y-4">
            {refinancingProcess.map((step, index) => (
              <div
                key={step.step}
                className="relative pl-0 md:pl-20"
              >
                {/* Timeline connector */}
                {index < refinancingProcess.length - 1 && (
                  <div className="hidden md:block absolute left-[1.625rem] top-16 h-full w-px bg-border" />
                )}

                <div className="flex flex-col md:flex-row gap-4 md:gap-0">
                  {/* Step indicator */}
                  <div className="flex items-center gap-4 md:absolute md:left-0 md:top-4">
                    <div className="flex-shrink-0 w-14 h-14 bg-primary text-primary-foreground rounded-2xl flex items-center justify-center font-serif text-xl font-medium">
                      {step.step}
                    </div>
                    <div className="md:hidden">
                      <h3 className="font-semibold">{step.title}</h3>
                      <span className="text-xs text-muted-foreground">
                        {step.duration}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="bg-muted/50 rounded-xl p-5 flex-1">
                    <div className="hidden md:flex items-center justify-between mb-2">
                      <h3 className="font-semibold">{step.title}</h3>
                      <span className="text-xs bg-white px-2 py-1 rounded-lg text-muted-foreground">
                        {step.duration}
                      </span>
                    </div>
                    <p className="text-muted-foreground text-sm">
                      {step.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 bg-accent/10 border border-accent/30 rounded-xl p-4">
            <p className="text-sm flex items-start gap-2">
              <FiCheck className="size-4 text-primary mt-0.5 flex-shrink-0" />
              <span>
                <strong>Total time:</strong> Most refinances complete within 2-4
                weeks. We handle the heavy lifting so you can focus on your day.
              </span>
            </p>
          </div>
        </div>
      </section>

      {/* Costs */}
      <section className="py-16 md:py-20 bg-cream">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="mb-12">
            <p className="section-label mb-4">Costs</p>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">
              What does refinancing cost?
            </h2>
            <p className="text-muted-foreground">
              Understand the potential costs so you can calculate if refinancing
              is worth it.
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-border/50 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-muted/50">
                  <th className="text-left p-4 font-medium">Cost</th>
                  <th className="text-left p-4 font-medium">Typical Range</th>
                  <th className="text-left p-4 font-medium hidden md:table-cell">
                    Notes
                  </th>
                </tr>
              </thead>
              <tbody>
                {costs.map((cost, index) => (
                  <tr
                    key={cost.name}
                    className={index !== costs.length - 1 ? "border-b border-border" : ""}
                  >
                    <td className="p-4">
                      <span className="font-medium">{cost.name}</span>
                      <p className="text-sm text-muted-foreground md:hidden mt-1">
                        {cost.description}
                      </p>
                    </td>
                    <td className="p-4 text-muted-foreground">{cost.typical}</td>
                    <td className="p-4 text-sm text-muted-foreground hidden md:table-cell">
                      {cost.description}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 bg-primary/5 border border-primary/20 rounded-xl p-4">
            <p className="text-sm flex items-start gap-2">
              <FiAlertCircle className="size-4 text-primary mt-0.5 flex-shrink-0" />
              <span>
                <strong>Break costs warning:</strong> If you're on a fixed rate,
                break costs can be substantial – sometimes tens of thousands of
                dollars. Always get a break cost estimate before proceeding.
              </span>
            </p>
          </div>
        </div>
      </section>

      {/* When NOT to refinance */}
      <section className="py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="mb-8">
            <p className="section-label mb-4">Caution</p>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">
              When refinancing might not make sense
            </h2>
          </div>

          <div className="space-y-4">
            {whenNotToRefinance.map((item) => (
              <div
                key={item.scenario}
                className="flex items-start gap-4 bg-muted/30 rounded-xl p-5"
              >
                <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                  <FiAlertCircle className="size-4 text-red-500" />
                </div>
                <div>
                  <h3 className="font-medium mb-1">{item.scenario}</h3>
                  <p className="text-sm text-muted-foreground">{item.why}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 2025 context */}
      <section className="py-16 md:py-20 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <p className="section-label mb-4">Market Update</p>
          <h2 className="font-serif text-3xl md:text-4xl mb-6">
            Refinancing in 2026
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white/10 rounded-xl p-6">
              <h3 className="font-semibold mb-3">RBA cash rate: 4.10%</h3>
              <p className="text-primary-foreground/80 text-sm">
                The RBA cut rates in February 2025. Lenders are competing for
                refinancers with sharper rates and cashback offers.
              </p>
            </div>
            <div className="bg-white/10 rounded-xl p-6">
              <h3 className="font-semibold mb-3">Cashback deals available</h3>
              <p className="text-primary-foreground/80 text-sm">
                Many lenders are offering cashback incentives of $2,000-$4,000+
                to attract refinancers. These can offset switching costs.
              </p>
            </div>
          </div>
          <p className="mt-6 text-primary-foreground/90 text-sm">
            Market conditions change frequently. We stay across the latest offers
            so you don't have to.
          </p>
        </div>
      </section>

      {/* CTA */}
      <CTASection
        label="Is it time to refinance?"
        title="Get a free comparison and see how much you could save"
        description="No obligation, no cost. We'll show you the numbers so you can decide."
        primaryAction={{ label: "Get Comparison", href: "/contact" }}
        secondaryAction={{
          label: "Calculators",
          href: "/calculators",
        }}
      />
    </>
  );
}
